import { CalendarBaseIcon } from '@tableau/icons/CalendarBaseIcon';
import { CollectionBaseIcon } from '@tableau/icons/CollectionBaseIcon';
import { DatasourceCanBaseIcon } from '@tableau/icons/DatasourceCanBaseIcon';
import { DatasourceServerConnectionBaseIcon } from '@tableau/icons/DatasourceServerConnectionBaseIcon';
import { FlowBaseIcon } from '@tableau/icons/FlowBaseIcon';
import { FolderOutlineOpenIcon } from '@tableau/icons/FolderOutlineOpenIcon';
import { GlobeBaseIcon } from '@tableau/icons/GlobeBaseIcon';
import { TableauFileWorkbookBaseIcon } from '@tableau/icons/TableauFileWorkbookBaseIcon';
import { UploadBaseIcon } from '@tableau/icons/UploadBaseIcon';
import React from 'react';
import { Action as ReduxAction } from 'redux';

import { FeatureFlags } from '~commonInfra/featureFlags/FeatureFlags';
import { hideLaunchEditOnMobile } from '~commonInfra/utils/MobileHelper';
import { SessionServerSelectors, SessionSiteSelectors, SessionUserSelectors } from '~src/bootstrap/redux/selectors/SessionInfoSelectors';
import { CollectionUtils } from '~src/Collections/utils/CollectionUtils';
import { IDataSourceDetailInfo } from '~src/datasource/models/IDataSourceInfo';
import { IProjectActions } from '~src/projects/ServerApi/types/IProjectActions';
import { ExploreContentApi_ContentType } from '~src/spacesPlatform/models/IContent';

import { AppState } from '../../../../bootstrap/redux/state/AppState';
import { Messages } from '../../../../codegen/Localize';
import { IDatasourceActions } from '../../../../datasource/ServerApi/types/IDatasourceActions';
import { selectEditingFlowsEnabled } from '../../../../flow/selectors/FlowSelectors';
import { SiteRolesUtil } from '../../../../userManagement/services/resources/SiteRolesUtil';
import { SiteRoles } from '../../../../userManagement/users/serverApi/types/ISiteRole';
import {
  createNewFlow,
  createNewProject,
  createNewPublishedConnection,
  createNewPublishedDataSource,
  createNewSchedule,
  createNewSite,
  createNewWorkbook,
  createNoNameCollectionAction
} from '../../../actions/ContentExplorerCreateActions';
import { ExplorableItemType, SpaceInfoItemTypeValue } from '../../../models/ContentType';
import { ISpaceTypeInfo } from '../../../models/resourceInfoParts';
import { selectAvailableActionsForEntity } from '../../../selectors/EntityActionsSelectors';
import { selectSpaceInfoItem } from '../../../selectors/SpaceInfoSelectors';
import { SpaceType } from '../../../state/SpaceInfo';
import { CreateContentButtonLocations } from './CreateContentButton';

export type CreateContentType = ExploreContentApi_ContentType | ExplorableItemType | 'workbookUpload' | 'flowUpload';
export interface ICreateContentAction {
  contentType: CreateContentType;
  isVisible(state: AppState, spaceType: SpaceType, location: CreateContentButtonLocations): boolean;
  isClickable(state: AppState, projectId?: string): boolean;
  createReduxAction(projectId?: string): ReduxAction;
  icon: (props: any) => React.ReactSVGElement | null;
  title: (spaceType: SpaceType) => string;
  isDivider: boolean;
}

const CREATE_PUBLISHED_CONNECTION_ACTION_SUPPORTED_ROLES = [
  SiteRoles.Creator,
  SiteRoles.ServerAdministrator,
  SiteRoles.SiteAdministratorCreator,
];

const UPLOAD_FLOW_ACTION_SUPPORTED_ROLES = [
  SiteRoles.Creator,
  SiteRoles.ServerAdministrator,
  SiteRoles.SiteAdministratorCreator,
  SiteRoles.SupportUser,
];

const UPLOAD_WORKBOOK_ACTION_SUPPORTED_ROLES = [
  SiteRoles.Creator,
  SiteRoles.ExplorerCanPublish,
  SiteRoles.ServerAdministrator,
  SiteRoles.SiteAdministratorCreator,
  SiteRoles.SiteAdministratorExplorer,
  SiteRoles.SupportUser,
];

const CREATE_PUBLISHED_DATASOURCE_ACTION_SUPPORTED_ROLES = [
  SiteRoles.Creator,
  SiteRoles.ServerAdministrator,
  SiteRoles.SiteAdministratorCreator
];

export const CreateFlowAction: ICreateContentAction = {
  contentType: 'flow',
  title: (spaceType: SpaceType) => {
    if (spaceType === SpaceInfoItemTypeValue.datasource) {
      return Messages.createContentFlowUsingThisDataSourceMessage();
    }

    return Messages.flow();
  },
  icon: FlowBaseIcon,
  isVisible: (appState: AppState, spaceType: SpaceType) => selectEditingFlowsEnabled(appState) && spaceType !== 'personal',
  isClickable: (appState: AppState) => {
    const spaceItem: ISpaceTypeInfo = selectSpaceInfoItem(appState);
    if (spaceItem && spaceItem.contentType === SpaceInfoItemTypeValue.datasource) {
      // SDM data sources are not supported for flow creation
      const datasourceInfo = spaceItem as IDataSourceDetailInfo;
      if (datasourceInfo && datasourceInfo.connectionType === 'tuaSemanticLayer') {
        return false;
      }

      const availableActionsForEntity: IDatasourceActions | undefined = selectAvailableActionsForEntity(appState, { contentType: SpaceInfoItemTypeValue.datasource, id: spaceItem.id }) as IDatasourceActions;
      return !!availableActionsForEntity && availableActionsForEntity.webEditFlow;
    }
    return SessionUserSelectors.canWebEditFlow(appState);
  },
  createReduxAction: (projectId?: string): ReduxAction => createNewFlow(projectId),
  isDivider: false
};

export const CreateProjectAction: ICreateContentAction = {
  contentType: 'project',
  title: Messages.project,
  icon: FolderOutlineOpenIcon,
  isVisible: (_appState: AppState, spaceType: SpaceType) => {
    return spaceType !== SpaceInfoItemTypeValue.datasource &&
      spaceType !== 'personal' &&
      spaceType !== 'externalAssets';
  },
  isClickable: (appState: AppState, projectId?: string) => {

    // If the user is site or server admin they can create a project
    // We need to check for server/site admin  to check permission for top level site
    if (SessionUserSelectors.isSessionServerAdmin(appState)) {
      return true;
    }

    if (SiteRolesUtil.isSiteAdmin(SessionSiteSelectors.siteRole(appState))) {
      return true;
    }

    if (projectId) {
      const availableActions = selectAvailableActionsForEntity(appState, { contentType: 'project', id: projectId }) as IProjectActions;
      if (availableActions && availableActions.createProject) {
        return true;
      }
    }

    return false;
  },
  createReduxAction: (projectId?: string): ReduxAction => {
    return createNewProject(projectId);
  },
  isDivider: false
};

export const CreatePublishedDatasourceAction: ICreateContentAction = {
  contentType: 'datasource',
  title: Messages.createPublishedDatasource,
  icon: DatasourceCanBaseIcon,
  isVisible: (appState: AppState, spaceType: SpaceType) => {
    return CREATE_PUBLISHED_DATASOURCE_ACTION_SUPPORTED_ROLES.includes(SessionSiteSelectors.siteRole(appState)) &&
      spaceType !== 'personal' &&
      spaceType !== 'externalAssets';
  },
  isClickable: (appState: AppState, projectId?: string) => {
    return SessionUserSelectors.canAuthorOnWeb(appState);
  },
  createReduxAction: (projectId?: string): ReduxAction => {
    return createNewPublishedDataSource(projectId);
  },
  isDivider: false
};

export const CreatePublishedConnectionAction: ICreateContentAction = {
  contentType: 'virtualConnection',
  title: Messages.publishedConnection,
  icon: DatasourceServerConnectionBaseIcon,
  isVisible: (_appState: AppState, spaceType: SpaceType) => {
    return FeatureFlags.publishedConnectionEditorEnabled &&
      spaceType !== 'personal' &&
      spaceType !== SpaceInfoItemTypeValue.datasource &&
      spaceType !== 'externalAssets';
  },
  isClickable: (appState: AppState, projectId?: string) => {
    return CREATE_PUBLISHED_CONNECTION_ACTION_SUPPORTED_ROLES.includes(SessionSiteSelectors.siteRole(appState));
  },
  createReduxAction: (projectId?: string): ReduxAction => {
    return createNewPublishedConnection(projectId);
  },
  isDivider: false
};

export const CreateWorkbookAction: ICreateContentAction = {
  contentType: 'workbook',
  title: (spaceType: SpaceType) => {
    if (spaceType === SpaceInfoItemTypeValue.datasource) {
      return Messages.createContentWorkbookUsingThisDataSourceMessage();
    } else {
      return Messages.workbook();
    }
  },
  icon: TableauFileWorkbookBaseIcon,
  isVisible: (_appState: AppState) => {
    return !hideLaunchEditOnMobile();
  },
  isClickable: (appState: AppState) => {
    const spaceItem: ISpaceTypeInfo = selectSpaceInfoItem(appState);
    if (spaceItem && spaceItem.contentType === SpaceInfoItemTypeValue.datasource) {
      // Do the same check here as NewWorkbookEntityAction
      const availableActionsForEntity: IDatasourceActions | undefined = selectAvailableActionsForEntity(appState, { contentType: SpaceInfoItemTypeValue.datasource, id: spaceItem.id }) as IDatasourceActions;
      return !!availableActionsForEntity && availableActionsForEntity.launchWebAuthoring;
    }
    return SessionUserSelectors.canAuthorOnWeb(appState);
  },
  createReduxAction: (projectId?: string): ReduxAction => {
    return createNewWorkbook(projectId);
  },
  isDivider: false
};

export const CreateSiteAction = {
  title: () => Messages.newSite(),
  icon: GlobeBaseIcon,
  isVisible: (_appState: AppState, spaceType: SpaceType) => spaceType === 'sites',
  isClickable: (appState: AppState) => {
    return !SessionServerSelectors.siteAdminSettingsEnabled(appState);
  },
  createReduxAction: (): ReduxAction => {
    return createNewSite();
  },
  isDivider: false
};

export const CreateScheduleAction = {
  title: () => Messages.newSchedule(),
  icon: CalendarBaseIcon,
  isVisible: (appState: AppState, spaceType: SpaceType) => spaceType === 'schedules' && SessionUserSelectors.isSessionServerAdmin(appState),
  isClickable: () => true,
  createReduxAction: (type?: string, isEnabledForLinkedTasks?: boolean): ReduxAction => {
    return createNewSchedule(type, isEnabledForLinkedTasks);
  },
  isDivider: false
};

export const CreateUnnamedCollectionAction: ICreateContentAction = {
  contentType: 'collection',
  title: () => Messages.collection(),
  icon: CollectionBaseIcon,
  isVisible: (state: AppState, spaceType: SpaceType, location: CreateContentButtonLocations) => {
    return CollectionUtils.checkCollectionSiteSetting() && (spaceType === 'collections' || location === CreateContentButtonLocations.Home);
  },
  isClickable: () => true,
  createReduxAction: (projectId?: string): ReduxAction => {
    return createNoNameCollectionAction();
  },
  isDivider: false
};

export const UploadFlowCreateMenuAction: ICreateContentAction = {
  contentType: 'flowUpload',
  title: () => Messages.contentUpload_uploadFlow(),
  icon: UploadBaseIcon,
  isVisible: (state: AppState, spaceType: SpaceType) => {
    return (FeatureFlags.uploadFlowFromWebEnabled
      && UPLOAD_FLOW_ACTION_SUPPORTED_ROLES.includes(SessionSiteSelectors.siteRole(state)) &&
      spaceType !== 'personal'
    );
  },
  isClickable: () => true,
  createReduxAction: undefined,
  isDivider: false
};

export const UploadWorkbookCreateMenuAction: ICreateContentAction = {
  contentType: 'workbookUpload' as any,
  title: () => Messages.contentUpload_uploadWorkbook(),
  icon: UploadBaseIcon,
  isVisible: (state: AppState, spaceType: SpaceType) => {
    return (FeatureFlags.uploadWorkbookFromWebEnabled
      && UPLOAD_WORKBOOK_ACTION_SUPPORTED_ROLES.includes(SessionSiteSelectors.siteRole(state)) &&
      spaceType !== 'personal'
    );
  },
  isClickable: () => true,
  createReduxAction: undefined,  // see handleSelect in CreateContentButton.tsx
  isDivider: false
};

export const GenericDivider: ICreateContentAction = {
  contentType: undefined,
  title: () => '',
  icon: undefined,
  isVisible: () => true,
  isClickable: () => false,
  createReduxAction: undefined,
  isDivider: true
};

export const BeforeCollectionDivider: ICreateContentAction = {
  ...GenericDivider,
  isVisible: CreateUnnamedCollectionAction.isVisible
};

export const BeforeUploadWorkbookDivider: ICreateContentAction = {
  ...GenericDivider,
  isVisible: (state: AppState, spaceType: SpaceType, location: CreateContentButtonLocations) => {
    return UploadWorkbookCreateMenuAction.isVisible(state, spaceType, location) ||
      UploadFlowCreateMenuAction.isVisible(state, spaceType, location);
  }
};

export const OrderedCreateContentActions = [
  CreateProjectAction,
  CreateWorkbookAction,
  CreateFlowAction,
  CreatePublishedDatasourceAction,
  CreatePublishedConnectionAction,
  BeforeCollectionDivider,
  CreateUnnamedCollectionAction,
  BeforeUploadWorkbookDivider,
  UploadWorkbookCreateMenuAction,
  UploadFlowCreateMenuAction
];
